import { Link, useParams } from "react-router-dom";
import { useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { ArrowUpRight, Globe, Mail, Phone, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { PageHeader, Container, ArrowLink } from "@/components/shared/PageHeader";
import { EventRow } from "@/components/shared/EventCard";
import { EmptyState } from "@/components/shared/Status";
import { fetchEvent, fetchEvents, fetchSite } from "@/lib/api";
import { fmtDate, fmtRange, fmtTime, paragraphs, capitalize } from "@/lib/format";
import { Seo, eventJsonLd } from "@/components/shared/Seo";
import { TicketPanel } from "@/components/tickets/TicketPanel";
import { cn } from "@/lib/utils";
import { countdownState, eventImages } from "@/lib/media";
import { EventHero } from "@/components/events/EventHero";
import { ShareMenu } from "@/components/events/ShareMenu";
import { EventPeople } from "@/components/events/EventPeople";
import { EventQuote } from "@/components/events/EventQuote";
import { EventVenueCard } from "@/components/events/EventVenueCard";
import { EventPagination } from "@/components/events/EventPagination";
import { EventRail } from "@/components/events/EventRail";
import { richBody } from "@/lib/richtext";
import { EventStickyBar } from "@/components/events/EventStickyBar";
import { AddToCalendar } from "@/components/events/AddToCalendar";
import { useHeroScroll } from "@/components/events/useHeroScroll";
import { EventGallery } from "@/components/events/EventGallery";
import { EventProgramme } from "@/components/events/EventProgramme";
import { EventVideo } from "@/components/events/EventVideo";
import { EventPractical } from "@/components/events/EventPractical";
import { RelatedEvents } from "@/components/events/RelatedEvents";
import { Lightbox } from "@/components/shared/Lightbox";

const groupByMonth = (events = []) => {
  const out = [];
  for (const e of events) {
    const key = fmtDate(e.date, "MMMM yyyy");
    const last = out[out.length - 1];
    if (last && last.key === key) last.items.push(e);
    else out.push({ key, items: [e] });
  }
  return out;
};

export default function Events() {
  const { data: events, isLoading } = useQuery({ queryKey: ["events-all"], queryFn: () => fetchEvents({ limit: 100 }) });
  const { data: site } = useQuery({ queryKey: ["site"], queryFn: fetchSite });
  const groups = groupByMonth(events || []);
  return (
    <div data-testid="events-page">
      <Seo title="Program" description="Konsertar, teater, quiz, møte og marknader på Halsnøy Kulturhus. Sjå kva som skjer på huset framover – lista viser berre komande arrangement." image={events?.find((e) => e.image)?.image} />
      <PageHeader eyebrow="Program" title="Kva skjer på kulturhuset?" intro="Konsertar, festivalar, møte og marknader. Lista viser berre komande arrangement – det som er over, forsvinn av seg sjølv." />
      <Container className="py-6 sm:py-8">
        {isLoading ? (
          <div className="space-y-2">{[0, 1, 2].map((i) => <Skeleton key={i} className="h-24 rounded-[20px]" />)}</div>
        ) : events?.length ? (
          <div className="space-y-14" data-testid="events-grid">
            {groups.map((g) => (
              <div key={g.key} className="grid gap-6 lg:grid-cols-12 lg:gap-8">
                <div className="lg:col-span-3"><h2 className="t-h2 sticky top-28">{capitalize(g.key)}</h2></div>
                <div className="divide-y divide-border lg:col-span-9">{g.items.map((e) => <EventRow key={e.id} event={e} />)}</div>
              </div>
            ))}
          </div>
        ) : (
          <div className="grid gap-6 border-t border-border pt-8 lg:grid-cols-12 lg:gap-8" data-testid="events-empty">
            <div className="lg:col-span-7">
              <h2 className="t-h3">Ingen komande arrangement akkurat no.</h2>
              <p className="mt-2 max-w-xl text-[15px] leading-relaxed text-muted-foreground">Styret legg ut nye arrangement her så snart dei er klare. I mellomtida: følg oss på Facebook – eller lag noko sjølv. Huset er ope for alle som vil arrangere.</p>
            </div>
            <div className="flex flex-wrap gap-x-8 gap-y-3 lg:col-span-5 lg:justify-end">
              {site?.facebook_url && <ArrowLink href={site.facebook_url} target="_blank" rel="noopener noreferrer" up data-testid="events-empty-facebook-link">Følg oss på Facebook</ArrowLink>}
              <ArrowLink to="/arranger" data-testid="events-empty-arranger-link">Arranger noko sjølv</ArrowLink>
            </div>
          </div>
        )}
        <div className="mt-16 grid gap-6 border-t border-border pt-8 lg:grid-cols-12 lg:gap-8" data-testid="events-arranger-cta">
          <div className="lg:col-span-7">
            <p className="t-mono text-muted-foreground">For arrangørar</p>
            <h2 className="t-h3 mt-3 text-balance">Har du noko folk bør få oppleve? Huset er ope for alle som vil arrangere.</h2>
          </div>
          <div className="flex flex-wrap items-end gap-x-8 gap-y-3 lg:col-span-5 lg:justify-end">
            <ArrowLink to="/arranger" data-testid="events-arranger-link">Slik arrangerer du hos oss</ArrowLink>
          </div>
        </div>
      </Container>
    </div>
  );
}

export function EventDetail() {
  const { id } = useParams();
  const { data: ev, isLoading, isError } = useQuery({ queryKey: ["event", id], queryFn: () => fetchEvent(id) });
  const { data: site } = useQuery({ queryKey: ["site"], queryFn: fetchSite });
  const [lightbox, setLightbox] = useState(null);
  const heroRef = useRef(null);
  const heroPassed = useHeroScroll(heroRef, !!ev, false);
  if (isLoading) return <div><Container className="py-10"><Skeleton className="h-9 w-40 rounded-full" /><div className="mt-10 grid gap-10 lg:grid-cols-12"><div className="lg:col-span-7"><Skeleton className="h-4 w-56" /><Skeleton className="mt-6 h-16 w-full" /><Skeleton className="mt-3 h-16 w-4/5" /><Skeleton className="mt-8 h-5 w-2/3" /></div><Skeleton className="aspect-[4/3] rounded-[20px] lg:col-span-5 lg:aspect-[4/5]" /></div></Container></div>;
  if (isError || !ev) return <Container className="py-20"><EmptyState title="Fann ikkje arrangementet" text="Arrangementet finst ikkje lenger, eller er over." action={<Button asChild><Link to="/arrangement">Alle arrangement</Link></Button>} /></Container>;
  const images = eventImages(ev);
  const captions = ev.captions || {};
  const time = fmtTime(ev.start_time, ev.end_time);
  const norm = (t = "") => t.replace(/\s+/g, " ").trim().toLowerCase();
  const body = paragraphs(ev.description).filter((p, i, all) => !(all.length === 1 && i === 0 && norm(p) === norm(ev.summary)));  // the hero already said it
  const over = countdownState(ev).state === "over";
  const dropcap = (body[0] || "").length >= 140 && !/^\S{1,5}\.\s/.test(body[0]);
  const quoteAfter = body.length >= 2 ? Math.min(1, body.length - 1) : body.length;  // the pulled line sits after the opening paragraph(s)
  const rich = richBody(ev.description_html);  // formatted text from the editor, with heading anchors and a split for the quote
  const hasBody = rich ? true : body.length > 0;
  const railItems = [
    hasBody && { id: "om", label: "Omtale" },
    ...(rich?.headings || []),
    ev.people?.length && { id: "medverkande", label: "Medverkande" },
    ev.programme?.length && { id: "program", label: "Program" },
    ev.video_url && { id: "video", label: "Video" },
    ev.organizer?.name && { id: "arrangor", label: "Arrangør" },
    images.length > 1 && { id: "bilete", label: "Bilete" },
    { id: "praktisk", label: "Praktisk" },
  ].filter(Boolean);
  const words = (rich ? rich.text : body.join(" ")).split(/\s+/).filter(Boolean).length;
  return (
    <div data-testid="event-detail-page">
      <Seo
        title={ev.title}
        description={`${fmtRange(ev.date, ev.end_date)}${time ? ` ${time}` : ""}${ev.location ? ` · ${ev.location}` : ""}. ${ev.summary || ev.description || ""}`}
        image={images[0]}
        imageAlt={`${ev.title} – ${fmtRange(ev.date, ev.end_date)} på Halsnøy Kulturhus`}
        og={{ kind: "arrangement", id: ev.id }}
        type="article"
        publishedAt={ev.created_at}
        modifiedAt={ev.updated_at}
        jsonLd={eventJsonLd(ev, site)}
      />
      <EventHero ref={heroRef} ev={ev} images={images} captions={captions} onOpenGallery={setLightbox} />
      <EventStickyBar ev={ev} show={heroPassed} />
      <Container className="border-t border-border py-12 sm:py-16 lg:py-20">
        <div className={cn("grid gap-12 lg:grid-cols-12 lg:gap-8", ev.tickets_enabled && !over && "pb-24 lg:pb-0")}>
          <div className="hidden lg:col-span-3 lg:block">
            <div className="sticky top-28">
              <p className="t-mono text-muted-foreground">Om arrangementet</p>
              <EventRail items={railItems} words={words} className="mt-6" />
            </div>
          </div>
          <div className="min-w-0 scroll-mt-28 lg:col-span-5" id="om">
            {rich ? (
              <div data-testid="event-description" data-rich="true">
                <div className={cn("article-body", !rich.dropcap && "no-dropcap")} dangerouslySetInnerHTML={{ __html: rich.head }} />
                <EventQuote quote={ev.quote} by={ev.quote_by} />
                {rich.tail && <div className="article-body no-dropcap" dangerouslySetInnerHTML={{ __html: rich.tail }} />}
              </div>
            ) : body.length ? (
              <div className={cn("article-body", !dropcap && "no-dropcap")} data-testid="event-description">
                {body.slice(0, quoteAfter).map((p, i) => <p key={i}>{p}</p>)}
                <EventQuote quote={ev.quote} by={ev.quote_by} />
                {body.slice(quoteAfter).map((p, i) => <p key={`b${i}`}>{p}</p>)}
              </div>
            ) : (
              <>
                {!(ev.people?.length || ev.programme?.length || ev.quote) && <p className="text-[17px] leading-relaxed text-muted-foreground" data-testid="event-description">Meir informasjon kjem.</p>}
                <EventQuote quote={ev.quote} by={ev.quote_by} />
              </>
            )}
            <EventPeople people={ev.people} />
            <EventProgramme items={ev.programme} />
            <div id="video" className="scroll-mt-28"><EventVideo url={ev.video_url} title={ev.title} /></div>
            <OrganizerBlock organizer={ev.organizer} ticketUrl={!ev.tickets_enabled ? ev.ticket_url : null} externalUrl={ev.external_url} contact={ev.organizer_contact} />
          </div>
          <aside className="lg:col-span-4 lg:pl-4" data-testid="event-info-card">
            <div className="lg:sticky lg:top-28">
              {ev.tickets_enabled ? <TicketPanel eventId={ev.id} className="lg:static" mobileBar={!over} /> : <EntryCard ev={ev} />}
              <div className={cn("mt-4 grid gap-2", over ? "grid-cols-1" : "grid-cols-2")} data-testid="event-share-row">
                {!over && <AddToCalendar ev={ev} className="h-11 w-full justify-center rounded-[8px] px-3 text-[14px]" testId="event-ics" />}
                <ShareMenu ev={ev} iconOnlyOnMobile={false} className="h-11 w-full justify-center rounded-[8px] border border-border bg-transparent text-[14px] font-medium hover:bg-secondary" testId="event-share" />
              </div>
              <EventVenueCard ev={ev} className="mt-4" />
            </div>
          </aside>
        </div>
      </Container>
      <EventGallery images={images} captions={captions} display={ev.display} alt={ev.title} onOpen={setLightbox} />
      <EventPractical practical={ev.practical} location={ev.location} site={site} />
      <EventPagination currentId={ev.id} />
      <RelatedEvents currentId={ev.id} />
      <Lightbox images={images} index={lightbox} onChange={setLightbox} alt={ev.title} captions={captions} display={ev.display} testId="event-gallery" />
    </div>
  );
}

/** Who is behind the evening – name and website only; the house is the venue, the organizer is the host. */
const OrganizerBlock = ({ organizer, ticketUrl, externalUrl, contact }) => {
  if (!organizer?.name) return null;
  const website = organizer.website || externalUrl;
  return (
    <div id="arrangor" className="mt-12 flex scroll-mt-28 flex-wrap items-center justify-between gap-x-8 gap-y-4 rounded-[20px] border border-border bg-secondary/60 p-6 sm:p-7" data-testid="event-organizer-block">
      <div className="flex min-w-0 items-center gap-4">
        <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-ink text-paper"><Users className="h-5 w-5" /></span>
        <div className="min-w-0">
          <p className="t-mono text-muted-foreground">Arrangør</p>
          <p className="mt-1 break-words font-display text-[20px] font-semibold leading-tight tracking-[-0.01em]" data-testid="event-organizer-name">{organizer.name}</p>
          <p className="mt-0.5 text-[13.5px] text-muted-foreground">Arrangementet er i regi av arrangøren. Kulturhuset er vertskap.</p>
        </div>
      </div>
      <div className="flex flex-wrap gap-x-6 gap-y-2">
        {website && <a href={website} target="_blank" rel="noopener noreferrer" className="u-link inline-flex items-center gap-1.5 text-[14.5px] font-medium" data-testid="event-organizer-website"><Globe className="h-4 w-4" /> {organizer.website ? "Nettsida til arrangøren" : "Meir om arrangementet"} <ArrowUpRight className="h-3.5 w-3.5" /></a>}
        {contact?.email && <a href={`mailto:${contact.email}`} className="u-link inline-flex items-center gap-1.5 text-[14.5px] font-medium" data-testid="event-organizer-email"><Mail className="h-4 w-4" /> {contact.email}</a>}
        {contact?.phone && <a href={`tel:${contact.phone.replace(/\s+/g, "")}`} className="u-link inline-flex items-center gap-1.5 text-[14.5px] font-medium" data-testid="event-organizer-phone"><Phone className="h-4 w-4" /> {contact.phone}</a>}
        {ticketUrl && <a href={ticketUrl} target="_blank" rel="noopener noreferrer" className="u-link inline-flex items-center gap-1.5 text-[14.5px] font-medium" data-testid="event-organizer-ticket-link">Billettar / påmelding <ArrowUpRight className="h-3.5 w-3.5" /></a>}
      </div>
    </div>
  );
};

/** Aside for events without online tickets: price, how you get in, and the external link when there is one. */
const EntryCard = ({ ev }) => (
  <div className="rounded-[12px] border border-border bg-card p-5" data-testid="event-entry-card">
    <p className="t-mono text-muted-foreground">{ev.ticket_url ? "Billettar" : ev.price_text ? "Pris og inngang" : "Inngang"}</p>
    <p className="mt-2 font-display text-[28px] font-semibold leading-none tracking-[-0.02em]" data-testid="event-entry-price">{ev.price_text || "Ope for alle"}</p>
    <p className="mt-3 text-[14.5px] leading-relaxed text-muted-foreground">{ev.ticket_url ? "Billettar og påmelding skjer hos arrangøren." : ev.price_text ? "Billettar får du i døra hos arrangøren – om ikkje anna står i teksten." : "Ingen billett trengst – berre møt opp."}</p>
    {ev.ticket_url && <Button asChild size="lg" className="mt-5 w-full justify-between" data-testid="event-ticket-link"><a href={ev.ticket_url} target="_blank" rel="noopener noreferrer">Billettar / påmelding <ArrowUpRight className="h-4 w-4" /></a></Button>}
  </div>
);
